// „Privacy trigger" widget — na privacy stránce zobrazí stav souhlasu, datum,
// ID souhlasu a umožní ho změnit/odvolat. Renderuje se do vlastního shadow rootu
// (izolace od stránky). Návštěvník tak vidí a spravuje svůj souhlas — prokazatelnost.
import { buildTriggerStyles } from './styles';
import { I18n } from './i18n';
import type { CookieCategory, CookieDef, CookieGobblerConfig, Lang, StoredConsent } from './types';

export interface TriggerCallbacks {
  onChange: () => void;
  onWithdraw: () => void;
}

function ce(tag: string, cls?: string, text?: string): HTMLElement {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) n.textContent = text;
  return n;
}

/** Účel v jazyce widgetu; když chybí (import z Cookiebotu má jen jeden jazyk), vezme jiný dostupný. */
function purposeOf(c: CookieDef, lang: Lang): string {
  return c.purpose?.[lang] || Object.values(c.purpose ?? {}).find(Boolean) || '';
}

/** Tabulka deklarace cookies jedné kategorie (jako cookie declaration v Cookiebotu). */
function cookieTable(cookies: CookieDef[], i18n: I18n): HTMLElement {
  // Obal s vodorovným scrollem — na úzkém displeji tabulka nerozbije stránku.
  const wrap = ce('div', 'cg-trig-table-wrap');
  const table = ce('table', 'cg-trig-table');
  const thead = ce('thead');
  const head = ce('tr');
  for (const key of ['trigger.colName', 'trigger.colProvider', 'trigger.colPurpose', 'trigger.colExpiry', 'trigger.colType']) {
    head.appendChild(ce('th', undefined, i18n.t(key)));
  }
  thead.appendChild(head);
  const tbody = ce('tbody');
  for (const c of cookies) {
    const tr = ce('tr');
    tr.append(
      ce('td', 'cg-trig-cookie', c.name),
      ce('td', undefined, c.provider || c.domain || '—'),
      ce('td', undefined, purposeOf(c, i18n.lang) || '—'),
      ce('td', undefined, c.expiry || '—'),
      ce('td', undefined, i18n.t(`type.${c.type ?? 'cookie'}`)),
    );
    tbody.appendChild(tr);
  }
  table.append(thead, tbody);
  wrap.appendChild(table);
  return wrap;
}

export function renderTrigger(
  target: HTMLElement,
  i18n: I18n,
  config: CookieGobblerConfig,
  consent: StoredConsent | null,
  cb: TriggerCallbacks,
): void {
  const root = target.shadowRoot ?? target.attachShadow({ mode: 'open' });
  root.replaceChildren();

  const style = ce('style');
  style.textContent = buildTriggerStyles(config.theme ?? {});
  root.appendChild(style);

  const box = ce('div', 'cg-trigger');
  box.appendChild(ce('div', 'cg-trig-title', i18n.t('trigger.title')));

  // Bez souhlasu — jen výzva k nastavení.
  if (!consent) {
    box.appendChild(ce('p', 'cg-trig-none', i18n.t('trigger.none')));
    const actions = ce('div', 'cg-trig-actions');
    const change = ce('button', 'cg-trig-btn primary', i18n.t('trigger.change'));
    change.addEventListener('click', cb.onChange);
    actions.appendChild(change);
    box.append(actions);
    root.appendChild(box);
    return;
  }

  // Stav / datum / ID
  const grid = ce('div', 'cg-trig-grid');

  const stateField = ce('div', 'cg-trig-field');
  stateField.appendChild(ce('div', 'cg-trig-label', i18n.t('trigger.state')));
  const chips = ce('div', 'cg-trig-chips');
  for (const cat of config.categories) {
    const on = !!consent.state[cat.id];
    chips.appendChild(ce('span', 'cg-trig-chip' + (on ? ' on' : ''), i18n.catName(cat.id)));
  }
  stateField.appendChild(chips);
  grid.appendChild(stateField);

  const dateField = ce('div', 'cg-trig-field');
  dateField.appendChild(ce('div', 'cg-trig-label', i18n.t('trigger.date')));
  const locale = i18n.lang === 'cs' ? 'cs-CZ' : undefined;
  dateField.appendChild(ce('div', 'cg-trig-value', new Date(consent.timestamp).toLocaleString(locale)));
  grid.appendChild(dateField);

  const idField = ce('div', 'cg-trig-field');
  idField.appendChild(ce('div', 'cg-trig-label', i18n.t('trigger.id')));
  idField.appendChild(ce('div', 'cg-trig-value cg-trig-id', consent.id || '—'));
  grid.appendChild(idField);

  box.appendChild(grid);

  // Detaily (skryté) — vysvětlující text, domény, na které se souhlas vztahuje,
  // a popis kategorií.
  const details = ce('div', 'cg-trig-details');
  details.hidden = true;
  // Text je jeden víceřádkový klíč; odstavce odděluje prázdný řádek.
  for (const para of i18n.t('trigger.about').split(/\n\s*\n/)) {
    if (para.trim()) details.appendChild(ce('p', 'cg-trig-text', para.trim()));
  }
  // Souhlas platí pro celou skupinu domén (primární + aliasy).
  const domains = [...new Set([config.id, ...(config.aliases ?? [])].filter(Boolean).map((d) => d.toLowerCase()))];
  if (domains.length) {
    details.appendChild(ce('p', 'cg-trig-text', `${i18n.t('trigger.domains')} ${domains.join(', ')}`));
  }
  // Kategorie s výpisem jednotlivých cookies. Bez deklarace (config.cookies prázdné)
  // jen popisy — „0 cookies" u všech kategorií by bylo zavádějící.
  const cookies = config.cookies ?? [];
  const sections: { id: CookieCategory; name: string; desc: string }[] = config.categories.map((cat) => ({
    id: cat.id,
    name: i18n.catName(cat.id),
    desc: i18n.catDesc(cat.id),
  }));
  if (cookies.some((c) => c.category === 'unclassified')) {
    sections.push({ id: 'unclassified', name: i18n.t('cat.unclassified.name'), desc: i18n.t('cat.unclassified.desc') });
  }
  for (const s of sections) {
    const list = cookies.filter((c) => c.category === s.id);
    const block = ce('div', 'cg-trig-cat');
    block.appendChild(ce('div', 'cg-trig-value', cookies.length ? `${s.name} (${list.length})` : s.name));
    block.appendChild(ce('div', 'cg-trig-label', s.desc));
    if (list.length) block.appendChild(cookieTable(list, i18n));
    else if (cookies.length) block.appendChild(ce('p', 'cg-trig-empty', i18n.t('trigger.noCookies')));
    details.appendChild(block);
  }

  // Akce
  const actions = ce('div', 'cg-trig-actions');
  const change = ce('button', 'cg-trig-btn primary', i18n.t('trigger.change'));
  change.addEventListener('click', cb.onChange);
  const withdraw = ce('button', 'cg-trig-btn', i18n.t('trigger.withdraw'));
  withdraw.addEventListener('click', cb.onWithdraw);
  const detailsBtn = ce('button', 'cg-trig-btn', i18n.t('trigger.showDetails'));
  detailsBtn.addEventListener('click', () => {
    details.hidden = !details.hidden;
    detailsBtn.textContent = i18n.t(details.hidden ? 'trigger.showDetails' : 'trigger.hideDetails');
  });
  actions.append(change, withdraw, detailsBtn);

  box.append(actions, details);
  root.appendChild(box);
}
